/**
 * The rules of the controls screen, on plain data: which keys may be bound, and what a new binding
 * does to the others. Both players share one keyboard, so a key belongs to at most one action of
 * either player.
 *
 * The clash rule: the key is TAKEN from the action that had it. If that was the other action's only
 * key, the two SWAP instead: the other action gets this action's old first key, so no action is ever
 * left without a key.
 */

import type { KeyBindings } from '../../input/keyboard';
import { keyName } from './text';

export type KeyAction = keyof KeyBindings;
export type PlayerIndex = 0 | 1;
export type KeySet = readonly [KeyBindings, KeyBindings];

/** The order the controls screen lists them in. */
export const KEY_ACTIONS: readonly KeyAction[] = ['left', 'right', 'jump', 'fire'];

/** What each action is called on screen (the C64's fire button blows bubbles). */
export const ACTION_LABELS: Readonly<Record<KeyAction, string>> = {
  left: 'LEFT', right: 'RIGHT', jump: 'JUMP', fire: 'BLOW',
};

/** Keys an action keeps after a rebind: the new one first, then the old first key. */
export const MAX_KEYS = 2;

/**
 * Never bindable: Esc and Enter drive the menus and Esc cancels a rebind, P pauses, F shows the frame
 * meter, Q quits while paused, Tab moves the browser's focus. Backspace is the menus' other BACK key,
 * so a game key there would also go back a screen.
 */
export const RESERVED_KEYS: ReadonlySet<string> = new Set([
  'Escape', 'KeyP', 'KeyF', 'KeyQ', 'Enter', 'Tab', 'Backspace',
]);

/** What settings storage accepts as a key code (sanitize.ts would drop anything else). */
const CODE = /^[A-Za-z0-9]{1,24}$/;

export function isBindable(code: string): boolean {
  return CODE.test(code) && !RESERVED_KEYS.has(code);
}

/** Where a binding moved from, for the screen's note. */
export interface KeyMove {
  player: PlayerIndex;
  action: KeyAction;
  /** True: the two actions swapped (that action got this one's old first key). */
  swapped: boolean;
}

export interface RebindResult {
  keys: [KeyBindings, KeyBindings];
  /** The other action the key came from, or null if it was free. */
  moved: KeyMove | null;
}

const copy = (b: KeyBindings): KeyBindings => ({
  left: [...b.left], right: [...b.right], jump: [...b.jump], fire: [...b.fire],
});

/** Bind `code` to one player's action by the clash rule above; null if the key cannot be bound. */
export function rebind(keys: KeySet, player: PlayerIndex, action: KeyAction, code: string): RebindResult | null {
  if (!isBindable(code)) return null;
  const out: [KeyBindings, KeyBindings] = [copy(keys[0]), copy(keys[1])];
  const own = keys[player][action];
  /** What this action can hand over in a swap: its first key that is not the new one. */
  const spare = own.find((k) => k !== code);
  let give: string | undefined;
  let moved: KeyMove | null = null;
  for (const p of [0, 1] as const) {
    for (const a of KEY_ACTIONS) {
      if (p === player && a === action) continue;
      const list = out[p][a];
      if (!list.includes(code)) continue;
      const rest = list.filter((k) => k !== code);
      if (rest.length > 0 || spare === undefined || give !== undefined) {
        out[p] = { ...out[p], [a]: rest };
        moved = { player: p, action: a, swapped: false };
      } else {
        give = spare;
        out[p] = { ...out[p], [a]: [spare] };
        moved = { player: p, action: a, swapped: true };
      }
    }
  }
  const next = [code, ...own.filter((k) => k !== code && k !== give)].slice(0, MAX_KEYS);
  out[player] = { ...out[player], [action]: next };
  return { keys: out, moved };
}

/** Where `code` is bound now, if anywhere. */
export function ownerOf(keys: KeySet, code: string): { player: PlayerIndex; action: KeyAction } | null {
  for (const p of [0, 1] as const) {
    for (const a of KEY_ACTIONS) if (keys[p][a].includes(code)) return { player: p, action: a };
  }
  return null;
}

/** An action's keys as the screen shows them: up to two names, and how many more are bound. */
export function keysText(codes: readonly string[]): string {
  const shown = codes.slice(0, MAX_KEYS).map(keyName).join(', ');
  const more = codes.length - MAX_KEYS;
  return more > 0 ? `${shown} +${more}` : shown || '-';
}

/** "PLAYER 2 JUMP": an action's name in notes. */
export const actionName = (player: PlayerIndex, action: KeyAction): string =>
  `PLAYER ${player + 1} ${ACTION_LABELS[action]}`;
